/**
 * SSO Final Page (Renamed to force cache bust)
 * Handles the SSO redirect from the identity provider
 * Parses ID token and stores user info client-side (no backend)
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import styles from './SSOCallbackPage.module.css';

const SSOFinalPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { refreshAuth } = useAuth();
    const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
    const [errorMessage, setErrorMessage] = useState<string>('');

    // Ref to prevent double-execution in React Strict Mode
    const processedRef = React.useRef(false);

    useEffect(() => {
        // Determine backend URL
        const isDevelopment = import.meta.env.MODE === 'development';
        const backendUrl = isDevelopment
            ? (import.meta.env.VITE_API_URL_DEV || 'http://localhost:5000')
            : (import.meta.env.VITE_API_URL_PRODUCTION || window.location.origin);

        // Construct the endpoint URL for the OIDC middleware
        // We are posting to /api/signin-oidc because SWA allows POST to /api
        const postEndpoint = `${backendUrl}/api/signin-oidc`;

        const handleCallback = async () => {
            // Prevent double submission
            if (processedRef.current) {
                console.log('[SSOFinalPage] Callback already processing/processed, skipping...');
                return;
            }
            processedRef.current = true;

            try {
                console.log('Processing SSO Callback via Relay... [SSO_FINAL_V2]');
                console.log('Current URL:', window.location.href);

                // 1. Capture data from Fragment (Hash) OR Query
                let hash = window.location.hash;
                if (hash.includes('#code=')) {
                    hash = hash.substring(hash.indexOf('#code=') + 1);
                } else if (hash.startsWith('#')) {
                    hash = hash.substring(1);
                }
                // Get params from both search and hash (to handle both Fragment and Query response modes)
                const currentSearchParams = new URLSearchParams(window.location.search);
                const hashParams = new URLSearchParams(window.location.hash.startsWith('#') ? window.location.hash.substring(1) : window.location.hash);

                const code = currentSearchParams.get('code') || hashParams.get('code');
                const idTokenFromUrl = currentSearchParams.get('id_token') || hashParams.get('id_token');
                const state = currentSearchParams.get('state') || hashParams.get('state');
                const sessionState = currentSearchParams.get('session_state') || hashParams.get('session_state'); // Also capture session_state

                console.log('SSO Callback Params:', {
                    hasCode: !!code,
                    hasIdToken: !!idTokenFromUrl,
                    hasState: !!state,
                    hasSessionState: !!sessionState,
                    search: window.location.search,
                    hash: window.location.hash
                });

                const idpError = currentSearchParams.get('error') || hashParams.get('error');
                const idpErrorDescription = currentSearchParams.get('error_description') || hashParams.get('error_description');

                if (idpError) {
                    throw new Error(idpErrorDescription || idpError);
                }

                // 2. Validate essential parameters
                if (!code && !idTokenFromUrl) {
                    console.log('No code or idTokenFromUrl found in fragment/query.');
                    throw new Error('No authentication response found.');
                }

                // 3. Construct Form Data to relay to Backend
                // The backend middleware (OpenIdConnectHandler) expects a FORM POST body.
                const formBody = new URLSearchParams();
                if (code) formBody.append('code', code);
                if (idTokenFromUrl) formBody.append('id_token', idTokenFromUrl);
                if (state) formBody.append('state', state);
                if (sessionState) formBody.append('session_state', sessionState);

                console.log(`Relaying authentication to ${postEndpoint}...`);

                // 4. POST to Backend
                // We use fetch with content-type application/x-www-form-urlencoded
                const response = await fetch(postEndpoint, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded'
                    },
                    body: formBody.toString(),
                    credentials: 'include' // CRITICAL: Ensure backend cookies are saved
                });

                if (!response.ok) {
                    let errorDetails = `Status: ${response.status}`;
                    try {
                        const errorJson = await response.json();
                        if (errorJson.details) {
                            errorDetails = errorJson.details;
                            console.error('Backend Auth Error Details:', errorJson.details);
                        } else if (errorJson.error) {
                            errorDetails = errorJson.error;
                        }
                    } catch (e) {
                        const text = await response.text();
                        errorDetails = text || errorDetails;
                    }
                    console.error('Backend rejected authentication:', response.status, errorDetails);
                    throw new Error(`Server authentication failed: ${errorDetails}`);
                }

                const data = await response.json();
                console.log('[SSOCallback] Backend Accepted. Tokens present:', { access: !!data.access_token, id: !!data.id_token });

                // Token-Based Auth Support (Private Browser Fix)
                // Use id_token as the access token because it's a valid JWT the backend can verify.
                // The actual 'access_token' from Microsoft is often opaque and invalid for our API.
                const tokenToUse = data.id_token || data.access_token;

                if (tokenToUse) {
                    const { authService } = await import('../services/authService');
                    authService.setAccessToken(tokenToUse);

                    if (data.user) {
                        // Manually seed user session to ensure immediate availability
                        sessionStorage.setItem('ssoUser', JSON.stringify(data.user));
                    }
                }

                setStatus('success');

                // 6. Refresh Client Context
                console.log('[SSOCallback] Refreshing authentication status...');
                await refreshAuth();

                // 7. Verify authentication was successful by checking auth status again
                await new Promise(resolve => setTimeout(resolve, 300));

                const { authService } = await import('../services/authService');
                // getAuthStatus will now send the Bearer token if available
                const authStatus = await authService.getAuthStatus();

                if (!authStatus.isAuthenticated && !tokenToUse) {
                    console.error('[SSOCallback] Authentication verification failed after refresh');
                    throw new Error('Authentication verification failed. Please try logging in again.');
                }

                if (!authStatus.isAuthenticated && tokenToUse) {
                    console.warn('[SSOCallback] Verification failed but we have a token. Proceeding with local session.');
                }

                console.log('[SSOCallback] Authentication verified successfully');

                const returnUrl = sessionStorage.getItem('ssoReturnUrl') || '/';
                sessionStorage.removeItem('ssoReturnUrl');

                console.log('[SSOCallback] Navigating to:', returnUrl);
                navigate(returnUrl, { replace: true });

            } catch (error: any) {
                console.error('SSO callback relay error:', error);
                setStatus('error');
                // Capture full error details for debugging
                const details = error instanceof Error ? error.message : JSON.stringify(error);

                // Ensure the error message is clear and definitely from THIS file
                setErrorMessage(`[SSO_ERROR] ${details}`);

                // Extended timeout to allow reading the error
                setTimeout(() => navigate('/'), 10000);
            }
        };

        handleCallback();
    }, [searchParams, navigate, refreshAuth]);

    return (
        <div className={styles.container}>
            <div className={styles.card}>
                <div style={{ fontSize: '10px', color: '#999', position: 'absolute', top: '5px', right: '5px' }}>
                    SSO_FINAL_DEBUG
                </div>
                {status === 'processing' && (
                    <>
                        <div className={styles.spinner}></div>
                        <h2>Completing Sign In...</h2>
                        <p>Securely exchanging tokens with server.</p>
                    </>
                )}

                {status === 'success' && (
                    <>
                        <div className={styles.successIcon}>✓</div>
                        <h2>Success!</h2>
                        <p>You are now logged in.</p>
                    </>
                )}

                {status === 'error' && (
                    <>
                        <div className={styles.errorIcon}>✗</div>
                        <h2>Sign In Failed</h2>
                        <p className={styles.errorMessage}>{errorMessage}</p>
                        <p>Redirecting you to home...</p>

                        <div style={{ marginTop: '20px', textAlign: 'left', fontSize: '10px', background: '#ffebee', padding: '10px', borderRadius: '4px', overflow: 'auto', maxHeight: '200px' }}>
                            <strong>Debug Details:</strong>
                            <pre style={{ whiteSpace: 'pre-wrap' }}>
                                URL: {window.location.href}
                                Hash: {window.location.hash}
                                Error: {errorMessage}
                            </pre>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default SSOFinalPage;
